<script setup lang="ts">
import { ref, onMounted, inject, Ref, watch } from 'vue'
import { useAuditLog } from '@/services/AuditLog'
import { AuditLogFilters } from '@/types/AuditLog'
import { Member } from '@/types/Member'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ScrollText, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-vue-next'

const { entries, totalCount, loading, error, getAuditLog, getActionTypes, getEntityTypes } = useAuditLog()
const vhostId = inject<Ref<string> | null>('vhostId', null)
const userInfo = inject<Ref<Member> | null>('userInfo', null)

const searchQuery = ref('')
const actionFilter = ref('all')
const entityFilter = ref('all')
const currentPage = ref(1)
const pageSize = 50

const actionTypes = getActionTypes()
const entityTypes = getEntityTypes()

const getActionColor = (action: string) => {
	if (action === 'delete' || action === 'deny') {
		return 'destructive'
	}

	if (action === 'create' || action === 'approve' || action === 'upload') {
		return 'default'
	}

	if (action === 'update' || action === 'settings_change') {
		return 'secondary'
	}

	return 'outline'
}

const fetchData = async () => {
	if (!vhostId || !vhostId.value) {
		return
	}

	const filters: AuditLogFilters = {}

	if (searchQuery.value) {
		filters.search = searchQuery.value
	}

	if (actionFilter.value !== 'all') {
		filters.action = actionFilter.value
	}

	if (entityFilter.value !== 'all') {
		filters.entityType = entityFilter.value
	}

	const start = (currentPage.value - 1) * pageSize

	await getAuditLog(vhostId.value, filters, start, pageSize)
}

const totalPages = ref(1)

watch(totalCount, (count) => {
	totalPages.value = Math.max(1, Math.ceil(count / pageSize))
})

const nextPage = () => {
	if (currentPage.value < totalPages.value) {
		currentPage.value++
		fetchData()
	}
}

const prevPage = () => {
	if (currentPage.value > 1) {
		currentPage.value--
		fetchData()
	}
}

const applyFilters = () => {
	currentPage.value = 1
	fetchData()
}

watch([actionFilter, entityFilter], () => {
	applyFilters()
})

onMounted(() => {
	fetchData()
})
</script>

<template>
	<div class="p-4 space-y-4">
		<div class="flex justify-between items-center">
			<div>
				<h2 class="text-2xl font-light">Audit Log</h2>
				<p class="text-sm text-muted-foreground">Track all actions and changes on the platform</p>
			</div>
			<Button variant="outline" @click="fetchData">
				<RefreshCw class="h-4 w-4 mr-2" />
				Refresh
			</Button>
		</div>

		<Card>
			<CardHeader>
				<div class="flex justify-between items-center flex-wrap gap-3">
					<div class="flex items-center space-x-3">
						<Input v-model="searchQuery" placeholder="Search logs..." class="w-64" @keyup.enter="applyFilters" />
						<Select v-model="actionFilter">
							<SelectTrigger class="w-40">
								<SelectValue placeholder="Action" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All Actions</SelectItem>
								<SelectItem v-for="action in actionTypes" :key="action" :value="action">
									{{ action.replace('_', ' ') }}
								</SelectItem>
							</SelectContent>
						</Select>
						<Select v-model="entityFilter">
							<SelectTrigger class="w-40">
								<SelectValue placeholder="Entity" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All Types</SelectItem>
								<SelectItem v-for="entity in entityTypes" :key="entity" :value="entity">
									{{ entity }}
								</SelectItem>
							</SelectContent>
						</Select>
						<Button variant="outline" @click="applyFilters">Search</Button>
					</div>
					<div class="text-sm text-muted-foreground">
						{{ totalCount }} total entries
					</div>
				</div>
			</CardHeader>
			<CardContent>
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Timestamp</TableHead>
							<TableHead>User</TableHead>
							<TableHead>Action</TableHead>
							<TableHead>Entity</TableHead>
							<TableHead>Details</TableHead>
							<TableHead>IP Address</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						<template v-if="entries.length > 0">
							<TableRow v-for="entry in entries" :key="entry.id">
								<TableCell class="text-sm text-muted-foreground whitespace-nowrap">{{ entry.timestamp }}</TableCell>
								<TableCell class="font-medium">{{ entry.username }}</TableCell>
								<TableCell>
									<Badge :variant="getActionColor(entry.action)">{{ entry.action }}</Badge>
								</TableCell>
								<TableCell>
									<div>
										<Badge variant="outline">{{ entry.entityType }}</Badge>
										<span v-if="entry.entityName" class="ml-2 text-sm">{{ entry.entityName }}</span>
										<span v-else-if="entry.entityId" class="ml-2 text-sm text-muted-foreground">#{{ entry.entityId }}</span>
									</div>
								</TableCell>
								<TableCell class="text-sm text-muted-foreground max-w-[200px] truncate">{{ entry.details || '-' }}</TableCell>
								<TableCell class="text-sm text-muted-foreground">{{ entry.ipAddress || '-' }}</TableCell>
							</TableRow>
						</template>
						<TableRow v-else>
							<TableCell colspan="6" class="h-24 text-center text-muted-foreground">
								<div v-if="loading">Loading audit log...</div>
								<div v-else>No audit log entries found.</div>
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>

				<div class="flex justify-between items-center mt-4" v-if="totalPages > 1">
					<div class="text-sm text-muted-foreground">
						Page {{ currentPage }} of {{ totalPages }}
					</div>
					<div class="flex space-x-2">
						<Button variant="outline" size="sm" :disabled="currentPage <= 1" @click="prevPage">
							<ChevronLeft class="h-4 w-4 mr-1" />
							Previous
						</Button>
						<Button variant="outline" size="sm" :disabled="currentPage >= totalPages" @click="nextPage">
							Next
							<ChevronRight class="h-4 w-4 ml-1" />
						</Button>
					</div>
				</div>
			</CardContent>
		</Card>
	</div>
</template>
